import { useState, useEffect, useRef } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import LoadingButton from "../BaseComponents/LoadingButton/LoadingButton";
import { useCheckEmailExists } from "../../api/people/usePeopleApi";
import styles from "./AddPrivateCustomerModal.module.css";

const NATIONALITIES = [
  "American", "Australian", "Austrian", "Belgian", "Brazilian", "British",
  "Canadian", "Chinese", "Czech", "Danish", "Dutch", "Finnish", "French",
  "German", "Greek", "Hungarian", "Indian", "Irish", "Italian", "Japanese",
  "Korean", "Mexican", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Swiss", "Turkish", "Ukrainian",
].sort();

const LANGUAGES = [
  "Arabic", "Chinese", "Danish", "Dutch", "English", "Finnish",
  "French", "German", "Greek", "Hungarian", "Italian", "Japanese",
  "Korean", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Turkish", "Ukrainian",
].sort();

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];

const COUNTRIES = [
  "Austria", "Belgium", "Brazil", "Canada", "China", "Czech Republic",
  "Denmark", "Finland", "France", "Germany", "Greece", "Hungary",
  "India", "Ireland", "Italy", "Japan", "Mexico", "Netherlands",
  "Norway", "Poland", "Portugal", "Romania", "Russia", "South Korea",
  "Spain", "Sweden", "Switzerland", "Turkey", "Ukraine",
  "United Kingdom", "United States",
].sort();

const emptyForm = {
  firstName:   "",
  lastName:    "",
  hasEmail:    true,
  email:       "",
  phone:       "",
  dateOfBirth: "",
  gender:      "",
  nationality: "",
  language:    "",
  street:      "",
  houseNumber: "",
  boxNumber:   "",
  city:        "",
  zip:         "",
  country:     "",
  status:      "Active",
};

const REQUIRED_FIELDS = ["firstName", "lastName", "phone", "nationality", "language"];

// Phone formatting helpers
const normalizePhone = (val) => val.replace(/[^\d+]/g, '');
const formatPhoneDisplay = (val) => {
  if (!val) return '';
  const n = normalizePhone(val);
  // Format: +XX XXX XXX XXX (generic grouping)
  if (n.length > 3) {
    let formatted = n.slice(0, 3);
    for (let i = 3; i < n.length; i += 3) {
      formatted += ' ' + n.slice(i, i + 3);
    }
    return formatted;
  }
  return n;
};

const validateField = (key, value, form) => {
  switch (key) {
    case "firstName":
      if (!value || !value.trim())  return "First name is required";
      if (value.trim().length < 2)  return "First name must be at least 2 characters";
      break;
    case "lastName":
      if (!value || !value.trim())  return "Last name is required";
      if (value.trim().length < 2)  return "Last name must be at least 2 characters";
      break;
    case "email":
      if (form && !form.hasEmail)   return ""; // No validation when hasEmail=false
      if (!value || !value.trim())  return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Invalid email format";
      break;
    case "phone":
      if (!value || !value.trim())  return "Phone number is required";
      { const n = normalizePhone(value);
        if (!n.startsWith('+'))     return "Phone must start with country code (e.g. +32)";
        if (!/^\+[1-9]\d{6,14}$/.test(n)) return "Invalid phone format. Use E.164 (e.g. +32475123456)";
      }
      break;
    case "nationality":
      if (!value)                   return "Nationality is required";
      break;
    case "language":
      if (!value)                   return "Language is required";
      break;
    default: break;
  }
  return "";
};

const AddPrivateCustomerModal = ({ open, onClose, onSave, customer = null, apiError = null }) => {
  const [form, setForm]       = useState(emptyForm);
  const [errors, setErrors]   = useState({});
  const [touched, setTouched] = useState({});
  const [emailWarning, setEmailWarning] = useState("");
  const emailCheckTimer = useRef(null);
  const { checkEmail } = useCheckEmailExists();

  const isEdit = !!customer;

  useEffect(() => {
    if (!open) return;
    if (customer) {
      setForm({
        firstName:   customer.firstName        ?? "",
        lastName:    customer.lastName         ?? "",
        hasEmail:    customer.hasEmail !== false,
        email:       customer.email            ?? "",
        phone:       customer.phone ? formatPhoneDisplay(customer.phone) : "",
        dateOfBirth: customer.dateOfBirth      ?? "",
        gender:      customer.gender           ?? "",
        nationality: customer.nationality      ?? "",
        language:    customer.language         ?? "",
        street:      customer.address?.street  ?? "",
        houseNumber: customer.address?.houseNumber ?? "",
        boxNumber:   customer.address?.boxNumber   ?? "",
        city:        customer.address?.city    ?? "",
        zip:         customer.address?.zip     ?? "",
        country:     customer.address?.country ?? "",
        status:      customer.status           ?? "Active",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
    setEmailWarning("");
  }, [open, customer]);

  const handleChange = (key, value) => {
    // Force email to lowercase and strip stray whitespace (e.g. from pasting)
    if (key === "email") value = value.trim().toLowerCase();
    // Force last name to uppercase (SUNSKY convention: surnames are UPPERCASE,
    // e.g. "Francine BERNAERS"). Mirrors AddTravelerModal.
    if (key === "lastName") value = value.toUpperCase();
    // Auto-format phone on input
    if (key === "phone") value = formatPhoneDisplay(value);
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
    if (key === "email") setEmailWarning("");
  };

  const handleBlur = async (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    const err = validateField(key, form[key], form);
    setErrors(prev => ({ ...prev, [key]: err }));

    // Async email duplicate check on blur
    if (key === "email" && form.hasEmail && !err && form.email.trim()) {
      clearTimeout(emailCheckTimer.current);
      emailCheckTimer.current = setTimeout(async () => {
        const excludeCode = isEdit ? customer?.customerCode : undefined;
        const result = await checkEmail(form.email, excludeCode);
        if (result.exists) {
          const names = result.matches.map(m => m.name).join(', ');
          setEmailWarning(`This email already exists in the system (${names}). Duplicate emails are not allowed.`);
        } else {
          setEmailWarning("");
        }
      }, 300);
    }
  };

  const handleClose = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
    setEmailWarning("");
    onClose();
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newErrors = {};
    let isValid = true;

    // Validate required fields
    REQUIRED_FIELDS.forEach(key => {
      const err = validateField(key, form[key], form);
      if (err) { newErrors[key] = err; isValid = false; }
    });

    // Validate email if hasEmail is true
    if (form.hasEmail) {
      const emailErr = validateField("email", form.email, form);
      if (emailErr) { newErrors.email = emailErr; isValid = false; }
    }

    setErrors(newErrors);
    setTouched(Object.fromEntries([...REQUIRED_FIELDS, "email"].map(k => [k, true])));
    if (!isValid) return;

    // Block submit if email duplicate warning exists
    if (emailWarning) return;

    setSubmitting(true);
    try {
      const normalizedPhone = normalizePhone(form.phone);
      await onSave({
        firstName:   form.firstName.trim(),
        lastName:    form.lastName.trim(),
        hasEmail:    form.hasEmail,
        email:       form.hasEmail ? form.email.trim().toLowerCase() : "",
        phone:       normalizedPhone,
        dateOfBirth: form.dateOfBirth || undefined,
        gender:      form.gender      || undefined,
        nationality: form.nationality,
        language:    form.language,
        address: {
          street:      form.street.trim()      || undefined,
          houseNumber: form.houseNumber.trim()  || undefined,
          boxNumber:   form.boxNumber.trim()    || undefined,
          city:        form.city.trim()         || undefined,
          zip:         form.zip.trim()          || undefined,
          country:     form.country             || undefined,
        },
        status: form.status,
      });
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors = Object.values(errors).some(Boolean);

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Customer" : "Add New Customer"}
      width="800px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            Close
          </button>
          <LoadingButton className={styles.saveBtn} loading={submitting} loadingText="Saving…" onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Save Customer"}
          </LoadingButton>
        </>
      }
    >

      <div className={styles.iconHeader}>
        <div className={styles.icon}>👤</div>
        <div className={styles.headerText}>
          <h3>{isEdit ? "Edit Customer Details" : "Add New Private Customer"}</h3>
          <p>
            {isEdit
              ? "Update customer personal and contact information"
              : "Register a new private customer — the legal and financial counterparty for all bookings"}
          </p>
        </div>
      </div>

      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Save failed:</strong> {apiError}
          </div>
        </div>
      )}

      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, err]) =>
                err ? <li key={key}>{err}</li> : null
              )}
            </ul>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>👤 PERSONAL INFORMATION</h4>
        <div className={styles.formGrid}>

          <div className={styles.field}>
            <label>FIRST NAME <span className={styles.required}>*</span></label>
            <input
              value={form.firstName}
              onChange={e => handleChange("firstName", e.target.value)}
              onBlur={() => handleBlur("firstName")}
              placeholder="e.g. Sarah"
              className={errors.firstName ? styles.inputError : ""}
            />
            {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
          </div>

          <div className={styles.field}>
            <label>LAST NAME <span className={styles.required}>*</span></label>
            <input
              value={form.lastName}
              onChange={e => handleChange("lastName", e.target.value)}
              onBlur={() => handleBlur("lastName")}
              placeholder="e.g. Johnson"
              className={errors.lastName ? styles.inputError : ""}
            />
            {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
          </div>

          <div className={styles.field}>
            <label>DATE OF BIRTH</label>
            <input
              type="date"
              value={form.dateOfBirth}
              onChange={e => handleChange("dateOfBirth", e.target.value)}
              min="1900-01-01"
              max={new Date().toISOString().split("T")[0]}
            />
            <span className={styles.helpText}>Used for age verification and traveler classification</span>
          </div>

          <div className={styles.field}>
            <label>GENDER</label>
            <select value={form.gender} onChange={e => handleChange("gender", e.target.value)}>
              <option value="">Select gender</option>
              {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>NATIONALITY <span className={styles.required}>*</span></label>
            <select
              value={form.nationality}
              onChange={e => handleChange("nationality", e.target.value)}
              onBlur={() => handleBlur("nationality")}
              className={errors.nationality ? styles.inputError : ""}
            >
              <option value="">Select nationality</option>
              {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {errors.nationality && <span className={styles.errorText}>{errors.nationality}</span>}
          </div>

          <div className={styles.field}>
            <label>PREFERRED LANGUAGE <span className={styles.required}>*</span></label>
            <select
              value={form.language}
              onChange={e => handleChange("language", e.target.value)}
              onBlur={() => handleBlur("language")}
              className={errors.language ? styles.inputError : ""}
            >
              <option value="">Select language</option>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            {errors.language && <span className={styles.errorText}>{errors.language}</span>}
          </div>

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📬 CONTACT INFORMATION</h4>
        <div className={styles.formGrid}>

          <div className={styles.field}>
            <label>CLIENT HAS EMAIL ADDRESS</label>
            <div className={styles.yesNoToggle}>
              <button
                type="button"
                className={`${styles.yesNoBtn} ${form.hasEmail ? styles.yesNoBtnYes : ""}`}
                onClick={() => { handleChange("hasEmail", true); }}
              >YES</button>
              <button
                type="button"
                className={`${styles.yesNoBtn} ${!form.hasEmail ? styles.yesNoBtnNo : ""}`}
                onClick={() => { handleChange("hasEmail", false); handleChange("email", ""); setEmailWarning(""); setErrors(prev => ({ ...prev, email: "" })); }}
              >NO</button>
            </div>
          </div>

          <div className={styles.field} />

          {form.hasEmail && (
            <div className={styles.field}>
              <label>EMAIL <span className={styles.required}>*</span></label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                placeholder="e.g. sarah.j@email.com"
                className={errors.email || emailWarning ? styles.inputError : ""}
                style={{ textTransform: "lowercase" }}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              {emailWarning && (
                <div className={styles.emailWarning}>
                  <span>⚠️</span> <span>{emailWarning}</span>
                </div>
              )}
              <span className={styles.helpText}>Used for invoicing and all official communication. Always stored in lowercase.</span>
            </div>
          )}

          <div className={styles.field}>
            <label>PHONE <span className={styles.required}>*</span></label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => handleChange("phone", e.target.value)}
              onBlur={() => handleBlur("phone")}
              placeholder="e.g. +32 475 123 456"
              className={errors.phone ? styles.inputError : ""}
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
            <span className={styles.helpText}>International E.164 format with country code (e.g. +32475123456)</span>
            {form.phone && !errors.phone && (
              <span className={styles.phoneDisplay}>Stored as: {normalizePhone(form.phone)}</span>
            )}
          </div>

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          🏠 ADDRESS
          <span className={styles.optionalTag}> — Optional</span>
        </h4>
        <div className={styles.formGrid}>

          <div className={styles.fieldWide}>
            <label>STREET</label>
            <input
              value={form.street}
              onChange={e => handleChange("street", e.target.value)}
              placeholder="e.g. Rue de la Loi"
            />
          </div>

          <div className={styles.field}>
            <label>HOUSE NO.</label>
            <input
              value={form.houseNumber}
              onChange={e => handleChange("houseNumber", e.target.value)}
              placeholder="e.g. 42"
            />
          </div>

          <div className={styles.field}>
            <label>BOX NO.</label>
            <input
              value={form.boxNumber}
              onChange={e => handleChange("boxNumber", e.target.value)}
              placeholder="e.g. 3A"
            />
            <span className={styles.helpText}>Apartment, suite, or bus number</span>
          </div>

          <div className={styles.field}>
            <label>ZIP / POSTAL CODE</label>
            <input
              value={form.zip}
              onChange={e => handleChange("zip", e.target.value)}
              placeholder="e.g. 1000"
            />
          </div>

          <div className={styles.field}>
            <label>CITY</label>
            <input
              value={form.city}
              onChange={e => handleChange("city", e.target.value)}
              placeholder="e.g. Brussels"
            />
          </div>

          <div className={styles.fieldWide}>
            <label>COUNTRY</label>
            <select value={form.country} onChange={e => handleChange("country", e.target.value)}>
              <option value="">Select country</option>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>⚙️ STATUS</h4>
        <div className={styles.statusToggle}>
          <input
            type="checkbox"
            id="custStatus"
            checked={form.status === "Active"}
            onChange={e => handleChange("status", e.target.checked ? "Active" : "Inactive")}
            className={styles.toggleInput}
          />
          <label htmlFor="custStatus" className={styles.toggleLabel}>
            <span className={styles.toggleSwitch} />
            <span className={styles.toggleText}>
              {form.status === "Active" ? "Active" : "Inactive"}
            </span>
          </label>
        </div>
        <span className={styles.helpText}>Only active customers can be selected in new bookings</span>
      </div>

      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>A Customer is the legal and financial counterparty — they pay, receive invoices, and own the booking</li>
            <li>A Customer is never automatically created as a Traveler — these are separate identities</li>
            <li>Financial data (balances, credit notes) is always stored at Customer level only</li>
            <li>Email is optional (toggle YES/NO) — if provided, it must be unique across all customers</li>
          </ul>
        </div>
      </div>

    </BaseModal>
  );
};

export default AddPrivateCustomerModal;